"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
export default function Inquiry() {
  const {
    ref: word1Ref,
    isVisible: word1Visible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.2
  });
  const {
    ref: word2Ref,
    isVisible: word2Visible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.2
  });
  const {
    ref: word3Ref,
    isVisible: word3Visible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.2
  });
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3501" id="policyinquirycta" className="relative bg-background text-foreground overflow-hidden py-24 md:py-40 lg:py-[200px] px-6 md:px-12 lg:px-20">
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <span className="font-default text-[11px] uppercase tracking-[0.2em] block" style={{
        color: "var(--primary)",
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          START — INTAKE
        </span>
      </div>

      <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[2px] z-10" style={{
      background: "var(--primary)",
      boxShadow: "0 0 40px 8px var(--primary)"
    }} aria-hidden="true" />

      <div className="md:hidden absolute left-0 right-0 top-0 h-[2px] z-10" style={{
      background: "var(--primary)",
      boxShadow: "0 0 40px 4px var(--primary)"
    }} aria-hidden="true" />

      <div className="relative max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 lg:col-start-1">
            <h2 className="font-default font-bold uppercase leading-[0.9] text-foreground text-[56px] sm:text-[80px] md:text-[110px] lg:text-[140px]" style={{
            letterSpacing: "-0.04em"
          }}>
              <span ref={word1Ref} data-index={0} className="block transition-all duration-700 ease-out" style={{
              opacity: word1Visible ? 1 : 0,
              transform: word1Visible ? "translateY(0)" : "translateY(40px)",
              transitionDelay: "0ms"
            }}>
                QUESTIONS
              </span>
              <span ref={word2Ref} data-index={1} className="block transition-all duration-700 ease-out" style={{
              opacity: word2Visible ? 1 : 0,
              transform: word2Visible ? "translateY(0)" : "translateY(40px)",
              transitionDelay: "200ms"
            }}>
                ABOUT OUR
              </span>
              <span ref={word3Ref} data-index={2} className="block transition-all duration-700 ease-out" style={{
              opacity: word3Visible ? 1 : 0,
              transform: word3Visible ? "translateY(0)" : "translateY(40px)",
              transitionDelay: "400ms"
            }}>
                POLICIES
                <span className="inline-block align-baseline" style={{
                color: "var(--primary)",
                fontSize: "1.3em",
                lineHeight: 0
              }}>
                  ?
                </span>
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 flex lg:justify-end">
            <div ref={cardRef} className="w-full lg:max-w-[440px] p-8 md:p-10 border border-border backdrop-blur-sm transition-all duration-700 ease-out shadow-lg rounded-lg" style={{
            backgroundColor: "var(--card)",
            opacity: cardVisible ? 1 : 0,
            transform: cardVisible ? "translateX(0)" : "translateX(40px)",
            transitionDelay: "600ms"
          }}>
              <div className="font-default text-[11px] uppercase tracking-[0.2em] mb-6" style={{
              color: "var(--secondary)"
            }}>
                SECURE FAMILY SUPPORT
              </div>

              <p className="font-default font-normal text-base md:text-lg leading-relaxed text-foreground mb-8">
                Connect with our team to discuss supervised parenting time, monitored exchanges, or family support services tailored to your court-ordered requirements.
              </p>

              <FormProvider formId="reach_out_form_contact" sectionName="Policy Inquiry Section" defaultValues={{
              email: ""
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              }
            }}>
                <PolicyForm />
              </FormProvider>

              <div className="mt-5 font-default text-[11px] uppercase tracking-[0.18em]" style={{
              color: "var(--foreground)"
            }}>
                ARIZONA-BASED — PROFESSIONAL & NEUTRAL
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function PolicyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
        <p className="text-sm font-default text-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="email" placeholder="Your Email Address" className="h-12 bg-background border-border rounded-lg focus:ring-primary/50" {...field} />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 uppercase font-default font-bold text-sm tracking-[0.15em] shadow-md rounded-lg hover:opacity-90 transition-opacity" style={{
      backgroundColor: "#A3B18A",
      color: "white"
    }}>
        {isSubmitting ? <Loader className="animate-spin size-4" /> : <>
            SEND INQUIRY
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}